<template>
  <div>
    <template v-if="subcategoryTabData && subcategoryTabData.length">
      <intro :product-info="productData" />
      <pages-list v-if="productData && productData.items && productData.items.length" :items="productData.items" />
      <product-block
        v-if="productData && productData.fittingOptions"
        :productInfo="productCard(langJson[lang].fittingOptions, productData.fittingOptions)"
      />
      <product-block
        v-if="productData && productData.accessories"
        :productInfo="productCard(langJson[lang].accessories, productData.accessories)"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
  import { useRoute, useRouter } from 'vue-router';
  import { useMainStore } from '@/stores/main';
  import ProductBlock from '~/components/product/ProductBlock.vue';
  import InternalService from '~/services/internalService';
  import { useCookie } from 'nuxt/app';

  definePageMeta({ middleware: ['check-channel-nodes', 'check-token'] });

  const route = useRoute();
  const mainStore = useMainStore();
  const subcategoryTabData = computed(() => mainStore.subcategoryTabData);
  const langJson = computed(() => mainStore.langJson);
  const lang = computed(() => mainStore.lang);

  // Reactive params for watching
  const currentSubcategoryTab = computed(() =>
    Array.isArray(route.params.subcategoryTab) ? route.params.subcategoryTab[0] : route.params.subcategoryTab
  );
  const currentProduct = computed(() => (Array.isArray(route.params.product) ? route.params.product[0] : route.params.product));

  // Load initial data before useAsyncData (so immediate: false works)
  if (route.path && route.path.startsWith('/products')) {
    if (!Array.isArray(mainStore.categories) || mainStore.categories.length === 0) {
      await mainStore.serverInit({ route, params: route.params });
    }
    await mainStore.setSubcategoryTabData({ params: route.params, checkData: { check: false } });
  }

  // useAsyncData to fetch and set internal data and subcategoryTabData - auto-refetches on param changes
  const { data: asyncData } = await useAsyncData(
    () => `productData-${currentSubcategoryTab.value}-${currentProduct.value}`,
    async () => {
      await InternalService.checkAndLoad({
        route,
        msal: mainStore.$msal, // if you use msal in your store
        cookies: {
          get: (key) => useCookie(key).value,
          set: (key, value, opts) => {
            useCookie(key, opts).value = value;
          },
          remove: (key) => {
            useCookie(key).value = null;
          }
        }
      });
      const checkData = { check: true };
      if (mainStore.setSubcategoryTabData) {
        await mainStore.setSubcategoryTabData({ params: route.params, checkData });
      }
      return mainStore.subcategoryTabData;
    },
    {
      // Watch these params and refetch when they change
      watch: [currentSubcategoryTab, currentProduct],
      immediate: false // Prevents duplicate call on mount
    }
  );

  const productData = computed(() => {
    const data = Array.isArray(subcategoryTabData.value) ? subcategoryTabData.value : [];
    const found = data.find((p) => String(p.id) === String(route.params.product));
    if (found) return found;
    // Fallback: if product param missing or 'undefinedundefined', pick first ProductCard item with items
    const card = data.find((p) => p.productBlockType === 'ProductCard' && Array.isArray(p.items) && p.items.length);
    return card || null;
  });

  function productCard(title: any, items: any) {
    return {
      external: { title },
      items,
      productBlockType: 'ProductCard'
    };
  }

  onMounted(() => {
    // as we do not have banner on product page we need to update navigation offset
    // as navigation doesn't use tabs on product page we need to update its height
    // TODO: Replace with event bus or composable if needed
    // this.$root.$emit('check-navigation-parameters');
  });
</script>
